import Colors from 'enums/Colors';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCloudArrowDown } from 'react-icons/fa6';
import { Badge, Breadcrumb, Modal, notification, NotificationArgsProps, Popover } from 'antd';
import { MdOutlineFolder } from 'react-icons/md';
import { RiFolderDownloadFill } from 'react-icons/ri';
import { FaFileDownload } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { IsCustomMaxWidth, IsCustomMinWidth, IsMobileBiggerWidth } from 'core/util/WindowSize';
import { fetchStorageFile } from 'hooks/StoreFiles';
import downloadButtonIcon from '../assets/downloadButton.png';

const Styled = {
    Container: styled.div<{ isMobile: boolean }>`
        display: flex;
        max-width: 1440px;
        padding: ${({ isMobile }) => (isMobile ? '16px 16px 32px 16px' : '57px 73px 57px 73px')};
        align-self: center;
        column-gap: 20px;
    `,

    Content: styled.div`
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 1rem;
    `,

    Title: styled.h1`
        font-family: 'Barlow';
        font-size: 32px;
        font-weight: 600;
        line-height: 150%;
        letter-spacing: 0%;
        text-align: left;
        color: ${Colors.SoftBlue};
    `,

    Description: styled.p`
        font-family: 'Barlow';
        font-size: 17px;
        font-weight: 400;
        line-height: 160%;
        letter-spacing: 0%;
        text-align: left;
        max-width: 800px;
        margin-bottom: 20px;
        color: ${Colors.Blue};
    `,

    Img: styled.img`
        display: flex;
        height: 600px;
        width: 477px;
        align-self: center;
        justify-self: center;
        border-radius: 4px;
    `,

    Item: {
        Container: styled.div<{ isBlue: boolean }>`
            display: flex;
            align-items: center;
            padding: 20px 0px 20px 0px;
            justify-content: space-between;
            width: 100%;
            border-bottom: 1px solid #e9e9e9;
            height: 60px;
        `,
    },

    Folder: styled.div`
        display: flex;
        align-items: center;
        flex-direction: column;
        padding: 10px;
        width: 160px;
        text-align: center;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.5s;
        border: 1px solid ${Colors.Grey10};

        &:hover {
            background-color: ${Colors.Grey13};
        }
    `,

    File: styled.div`
        display: flex;
        flex-direction: column;
        padding: 10px;
        width: 100%;
        font-size: 16px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.5s;
        border: 1px solid ${Colors.Grey10};

        &:hover {
            background-color: ${Colors.Grey13};
        }

        @media screen and (max-width: 600px) {
            min-height: 80px;
        }
    `,
};

type NotificationPlacement = NotificationArgsProps['placement'];

type TArquivos = {
    arquivo: string;
    nome: string;
};

type TSubPasta = {
    titleSubPasta: string;
    arquivos?: TArquivos[];
    subPastas?: TSubPasta[];
};

type TMaterial = {
    isPaste?: boolean;
    titlePaste?: string;
    arquivos?: TArquivos[];
    subPastas?: TSubPasta[];
};

interface IItem {
    title: string;
    materiais: TMaterial[];
}

const Item = ({ title, materiais }: IItem) => {
    const [open, setOpen] = useState<boolean>(false);
    const [arquivos, setArquivos] = useState<TArquivos[]>([]);
    const [subPastas, setSubPastas] = useState<TSubPasta[]>([]);
    const [api, contextHolder] = notification.useNotification();
    const [abaArquivos, setAbaArquivos] = useState<boolean>(false);
    const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);

    const [navigationStack, setNavigationStack] = useState<
        {
            arquivos: TArquivos[];
            subPastas: TSubPasta[];
            title: string;
        }[]
    >([]);

    const openFolder = (title: string, arquivos?: TArquivos[], subPastas?: TSubPasta[]) => {
        const currentState = {
            title,
            arquivos: arquivos ?? [],
            subPastas: subPastas ?? [],
        };

        setNavigationStack(prev => [...prev, currentState]);

        setBreadcrumbs(prev => [...prev, title]);

        setArquivos(currentState.arquivos);
        setSubPastas(currentState.subPastas);

        setAbaArquivos(true);
    };

    const navigateToBreadcrumb = (index: number) => {
        const targetStack = navigationStack.slice(0, index + 1);

        const targetFolder = targetStack[targetStack.length - 1];

        setNavigationStack(targetStack);

        setBreadcrumbs(prev => prev.slice(0, index + 1));

        setArquivos(targetFolder.arquivos);
        setSubPastas(targetFolder.subPastas);
    };

    const resetToRoot = () => {
        const hasMultipleFolders = materiais.length > 1 && materiais.some(material => material.isPaste);

        setNavigationStack([]);
        setBreadcrumbs([]);

        if (hasMultipleFolders) {
            setArquivos([]);

            setSubPastas(
                materiais.map(material => ({
                    titleSubPasta: material.titlePaste ?? '',
                    arquivos: material.arquivos ?? [],
                    subPastas: material.subPastas ?? [],
                }))
            );
        } else {
            setArquivos(materiais[0]?.arquivos ?? []);
            setSubPastas(materiais[0]?.subPastas ?? []);
        }

        setAbaArquivos(false);
    };

    function sanitizeFileName(fileName: string): string {
        // Aqui, você pode adicionar uma lógica para remover caracteres não permitidos
        return fileName.replace(/[^\w\s\.\-]/gi, '');
    }

    // Mapeamento de tipos MIME para extensões de arquivo
    const mimeToExtension = (mime: string): string => {
        const mimeTypes: { [key: string]: string } = {
            'application/pdf': '.pdf',
            'application/vnd.ms-powerpoint': '.ppt',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
            'application/msword': '.doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
            'image/jpeg': '.jpg',
            'image/png': '.png',
            'image/gif': '.gif',
            'text/plain': '.txt',
            'application/zip': '.zip',
            'application/x-rar-compressed': '.rar',
            'application/json': '.json',
            // Adicione outros tipos conforme necessário
        };

        return mimeTypes[mime] || ''; // Retorna uma string vazia caso o tipo não tenha correspondência
    };

    const downloadMaterial = async (arquivo: string, nome: string) => {
        openNotification();
        try {
            if (!arquivo) {
                console.error('Arquivo não disponível');
                return;
            }

            const fileUrl = fetchStorageFile(arquivo); // Supondo que fetchStorageFile seja uma função já definida
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Network response was not ok');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const fileExtension = mimeToExtension(blob.type); // Obter a extensão do arquivo com base no tipo MIME
            const fileName = sanitizeFileName(nome) + (fileExtension || '.pptx'); // Adiciona .bin se não encontrar uma correspondência

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName; // Nome do arquivo com a extensão correta
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url); // Limpa a URL criada
        } catch (error) {
            console.error('Erro ao baixar o arquivo:', error);
        }
    };

    const addFilesToZip = async (folder: JSZip, arquivos: TArquivos[], subPastas: TSubPasta[]) => {
        // Arquivos da pasta atual
        for (const arquivo of arquivos) {
            try {
                const fileUrl = fetchStorageFile(arquivo.arquivo);

                const response = await fetch(fileUrl);

                if (!response.ok) {
                    throw new Error(response.statusText);
                }

                const blob = await response.blob();

                const extension = mimeToExtension(blob.type);

                const fileName = sanitizeFileName(arquivo.nome) + (extension || '.bin');

                folder.file(fileName, blob);
            } catch (error) {
                console.error(`Erro ao baixar ${arquivo.nome}`, error);
            }
        }

        // Subpastas recursivamente
        for (const subPasta of subPastas) {
            const subFolder = folder.folder(subPasta.titleSubPasta);

            if (subFolder) {
                await addFilesToZip(subFolder, subPasta.arquivos ?? [], subPasta.subPastas ?? []);
            }
        }
    };

    const downloadAllFilesAsZip = async () => {
        const zip = new JSZip();

        const zipName = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : title;

        const rootFolder = zip.folder(zipName);

        if (!rootFolder) return;

        if (breadcrumbs.length > 0) {
            // Usuário está dentro de uma pasta/subpasta
            await addFilesToZip(rootFolder, arquivos, subPastas);
        } else {
            // Tela inicial
            for (const material of materiais) {
                const folder = rootFolder.folder(material.titlePaste ?? 'materiais');

                if (!folder) continue;

                await addFilesToZip(folder, material.arquivos ?? [], material.subPastas ?? []);
            }
        }

        const content = await zip.generateAsync({
            type: 'blob',
        });

        saveAs(content, `${zipName}.zip`);
    };

    const openNotification = () => {
        api.info({
            message: 'Aguarde um momento.',
            description: 'Estamos preparando o material para você! ',
            duration: 0,
        });
    };

    const downloadAll = () => {
        openNotification();

        downloadAllFilesAsZip();

        setOpen(false);
    };

    return (
        <>
            {contextHolder}
            <Modal
                maskClosable={false}
                title={`Materiais - ${title}`}
                centered
                open={open}
                okText={abaArquivos ? 'Baixar todos os arquivos' : 'Baixar todos os materiais'}
                onOk={downloadAll}
                onCancel={() => {
                    if (navigationStack.length > 1) {
                        const updatedStack = [...navigationStack];

                        updatedStack.pop();

                        const previousFolder = updatedStack[updatedStack.length - 1];

                        setNavigationStack(updatedStack);

                        setBreadcrumbs(prev => prev.slice(0, -1));

                        setArquivos(previousFolder.arquivos);
                        setSubPastas(previousFolder.subPastas);
                    } else if (navigationStack.length === 1) {
                        resetToRoot();
                    } else {
                        setOpen(false);
                    }
                }}
                cancelText={abaArquivos ? 'Voltar' : 'Fechar'}
                width={IsCustomMaxWidth(600) ? '100%' : '50%'}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '0.2rem',
                        flexWrap: 'wrap',
                    }}
                >
                    {/* {!abaArquivos &&
                        materiais.map((material, index) => {
                            if (material.isPaste) {
                                return (
                                    <Popover
                                        key={index}
                                        content={
                                            <div onClick={downloadAll}>
                                                <p
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    Baixar pasta <img src={downloadButtonIcon} width={24} height={27} />
                                                </p>
                                            </div>
                                        }
                                        title="Baixe a pasta com todos os arquivos se desejar."
                                    >
                                        <Styled.Folder
                                            onClick={() => {
                                                if (material.subPastas?.length) {
                                                    openFolder(material.titlePaste ?? '', material.arquivos ?? [], material.subPastas);
                                                } else {
                                                    openFolder(material.titlePaste ?? '', material.arquivos ?? [], []);
                                                }
                                            }}
                                        >
                                            <Badge
                                                styles={{
                                                    root: {
                                                        top: 5,
                                                    },
                                                }}
                                                count={material.arquivos?.length ?? material.subPastas?.length ?? 0}
                                            >
                                                <MdOutlineFolder size={40} />
                                            </Badge>
                                            <p style={{ textAlign: 'center' }}>{material.titlePaste}</p>
                                        </Styled.Folder>
                                    </Popover>
                                );
                            }
                        })} */}

                    {abaArquivos && (
                        <Breadcrumb
                            style={{
                                cursor: 'pointer',
                                width: '100%',
                                marginBottom: '1rem',
                            }}
                        >
                            <Breadcrumb.Item onClick={resetToRoot}>Materias</Breadcrumb.Item>
                            {breadcrumbs.map((item, index) => (
                                <Breadcrumb.Item key={index} onClick={() => navigateToBreadcrumb(index)}>
                                    {item}
                                </Breadcrumb.Item>
                            ))}
                        </Breadcrumb>
                    )}

                    {subPastas.length > 0 &&
                        subPastas.map((subPasta, index) => (
                            <Styled.Folder key={index} onClick={() => openFolder(subPasta.titleSubPasta, subPasta.arquivos, subPasta.subPastas)}>
                                <Badge
                                    styles={{
                                        root: {
                                            top: 5,
                                        },
                                    }}
                                    count={subPasta.arquivos?.length ?? subPasta.subPastas?.length ?? 0}
                                >
                                    <MdOutlineFolder size={40} />
                                </Badge>

                                <p
                                    style={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {subPasta.titleSubPasta}
                                </p>
                            </Styled.Folder>
                        ))}
                    {arquivos.map((arq, index) => (
                        <Styled.File onClick={() => downloadMaterial(arq.arquivo, arq.nome)} key={index}>
                            <p
                                style={{
                                    fontSize: 16,
                                }}
                            >
                                <img src={downloadButtonIcon} width={24} height={27} /> {arq.nome}
                            </p>
                        </Styled.File>
                    ))}
                </div>
            </Modal>
            <Styled.Item.Container isBlue={true}>
                <p style={{ fontWeight: '500', fontFamily: 'Barlow', color: '#282828' }}>{title}</p>
                <p
                    onClick={() => {
                        setOpen(true);
                        resetToRoot();
                    }}
                    style={{ display: 'flex', gap: '0.5rem', cursor: 'pointer' }}
                >
                    <img src={downloadButtonIcon} width={24} height={27} />
                </p>
            </Styled.Item.Container>
        </>
    );
};

interface IMateriaisExclusivos {
    title: string;
    description: string;
    img: string;
    items: IItem[];
}

const MateriaisExclusivos = ({ title, description, img, items }: IMateriaisExclusivos) => {
    const imgStorage = fetchStorageFile(img);
    const isMobile = IsMobileBiggerWidth();

    return (
        <Styled.Container isMobile={isMobile}>
            <Styled.Content>
                <Styled.Title>{title}</Styled.Title>
                <Styled.Description
                    dangerouslySetInnerHTML={{
                        __html: description,
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            gap: '1rem',
                        }}
                    >
                        {items.length > 0 && items.map((item, index) => <Item key={index} title={item.title} materiais={item.materiais} />)}
                    </div>
                </div>
            </Styled.Content>
            {IsCustomMinWidth(1160) && <Styled.Img src={imgStorage} />}
        </Styled.Container>
    );
};

export default MateriaisExclusivos;
